import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import { initGA } from '../utils/analytics';
import { posthog, purgeDurablePostHogStorage } from '../lib/analytics/posthogClient';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface CookiePreferences {
  analytics: boolean;
  timestamp: number;
}

export interface ConsentContextType {
  /** Whether user has granted analytics consent */
  hasConsent: boolean;
  /** Whether the consent banner should be displayed */
  showBanner: boolean;
  /** Accept all cookies */
  acceptAll: () => void;
  /** Reject all optional cookies */
  rejectAll: () => void;
  /** Update individual preferences */
  updatePreferences: (prefs: { analytics: boolean }) => void;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                         */
/* ------------------------------------------------------------------ */

const STORAGE_KEY = 'buzzmap_cookie_consent';

/**
 * Consent granted: upgrade PostHog to durable, cookie-based tracking so the
 * same visitor is recognised across sessions (accurate unique users, retention,
 * and login identification). Anonymous events captured before this call are
 * seamlessly continued under the now-persistent identifier.
 */
function enableDurableAnalytics(): void {
  posthog.set_config({ persistence: 'localStorage+cookie' });
}

/**
 * Consent refused (or withdrawn): purge any stored identifier/cookie left over
 * from a previous acceptance, then fall back to in-memory, cookieless mode.
 * Anonymous aggregate traffic keeps being counted — no consent needed for that —
 * but nothing persistent is written to the browser.
 */
function disableCookieAnalytics(): void {
  posthog.reset();
  posthog.set_config({ persistence: 'memory' });
  // Se la libreria non è ancora caricata il reset sopra è solo in coda: i dati durevoli di una
  // sessione consentita si cancellano subito, senza aspettarla.
  purgeDurablePostHogStorage();
}

function readStoredConsent(): CookiePreferences | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'analytics' in parsed &&
      'timestamp' in parsed
    ) {
      return parsed as CookiePreferences;
    }
    return null;
  } catch {
    return null;
  }
}

function writeConsent(prefs: CookiePreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // localStorage may be unavailable (private browsing quota exceeded, etc.)
  }
}

/* ------------------------------------------------------------------ */
/*  Context                                                           */
/* ------------------------------------------------------------------ */

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

function getMeasurementId(): string {
  return import.meta.env.VITE_GA_MEASUREMENT_ID ?? '';
}

/* ------------------------------------------------------------------ */
/*  Provider                                                          */
/* ------------------------------------------------------------------ */

export function ConsentProvider({ children }: { children: ReactNode }) {
  const [hasConsent, setHasConsent] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  // On mount: read stored preference
  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      setHasConsent(stored.analytics);
      setShowBanner(false);

      // Initialise GA if consent was previously given
      if (stored.analytics && import.meta.env.PROD) {
        const id = getMeasurementId();
        if (id) initGA(id);
      }

      // Apply stored preference. Anonymous cookieless capture is already active
      // from init(); consent only decides whether to upgrade to durable tracking.
      if (stored.analytics) {
        enableDurableAnalytics();
      } else {
        posthog.set_config({ persistence: 'memory' });
      }
    } else {
      // No stored consent — banner is shown. Traffic is still counted
      // anonymously (cookieless) until the user decides.
      setShowBanner(true);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const prefs: CookiePreferences = { analytics: true, timestamp: Date.now() };
    writeConsent(prefs);
    setHasConsent(true);
    setShowBanner(false);

    if (import.meta.env.PROD) {
      const id = getMeasurementId();
      if (id) initGA(id);
    }

    enableDurableAnalytics();
  }, []);

  const rejectAll = useCallback(() => {
    const prefs: CookiePreferences = { analytics: false, timestamp: Date.now() };
    writeConsent(prefs);
    setHasConsent(false);
    setShowBanner(false);

    disableCookieAnalytics();
  }, []);

  const updatePreferences = useCallback((incoming: { analytics: boolean }) => {
    const prefs: CookiePreferences = {
      analytics: incoming.analytics,
      timestamp: Date.now(),
    };
    writeConsent(prefs);
    setHasConsent(incoming.analytics);
    setShowBanner(false);

    if (incoming.analytics && import.meta.env.PROD) {
      const id = getMeasurementId();
      if (id) initGA(id);
    }

    if (incoming.analytics) {
      enableDurableAnalytics();
    } else {
      disableCookieAnalytics();
    }
  }, []);

  const value = useMemo<ConsentContextType>(
    () => ({ hasConsent, showBanner, acceptAll, rejectAll, updatePreferences }),
    [hasConsent, showBanner, acceptAll, rejectAll, updatePreferences]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}

/* ------------------------------------------------------------------ */
/*  Hook                                                              */
/* ------------------------------------------------------------------ */

export function useConsent(): ConsentContextType {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
}
